import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';

import { RealtimeChannel } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';

type RealtimeStatus = 'connecting' | 'connected' | 'disconnected';

interface RealtimeContextType {
  status: RealtimeStatus;
  lastHeartbeat: Date | null;
  checkRealtime: () => void;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

const HEARTBEAT_TIMEOUT = 90 * 1000; // 90 seconds, cron should be every minute
const INITIAL_CONNECT_TIMEOUT = 15 * 1000; // 15 seconds for initial connection
const RETRY_DELAY = 30 * 1000; // 30 seconds before retrying

export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const disconnect = useCallback((reason: string) => {
    console.warn(`[RealtimeProvider] Disconnecting. Reason: ${reason}`);
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    setStatus('disconnected');
  }, []);

  const checkRealtime = useCallback(() => {
    disconnect('Manual check triggered');
    
    setStatus('connecting');
    console.log('[RealtimeProvider] Checking realtime connection...');
    
    const channel = supabase.channel('realtime-status');
    channelRef.current = channel;

    const handleHeartbeatMiss = () => {
      disconnect('Heartbeat missed');
      retryTimeoutRef.current = setTimeout(checkRealtime, RETRY_DELAY);
    };

    channel
      .on('broadcast', { event: 'ping' }, (message) => {
        console.log('[RealtimeProvider] Heartbeat received:', message.payload);
        setLastHeartbeat(new Date());
        if (status !== 'connected') {
          setStatus('connected');
        }
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = setTimeout(handleHeartbeatMiss, HEARTBEAT_TIMEOUT);
      })
      .subscribe((subStatus, err) => {
        if (subStatus === 'SUBSCRIBED') {
          console.log('[RealtimeProvider] Subscribed to heartbeat channel.');
          if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = setTimeout(handleHeartbeatMiss, INITIAL_CONNECT_TIMEOUT);
        } else if (subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT' || err) {
          console.error(`[RealtimeProvider] Subscription failed: ${subStatus}`, err);
          disconnect(`Subscription failed: ${subStatus}`);
          retryTimeoutRef.current = setTimeout(checkRealtime, RETRY_DELAY);
        }
      });
  }, [disconnect, status]);

  useEffect(() => {
    checkRealtime();
    return () => {
      disconnect('Component unmounted');
    };
  }, []);

  const value = { status, lastHeartbeat, checkRealtime };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};
